import { useId, useRef, useState } from 'react';

const scrollDirections = new Map<string, [number, number]>([
  ['ArrowUp', [0, -40]],
  ['ArrowDown', [0, 40]],
  ['ArrowLeft', [-40, 0]],
  ['ArrowRight', [40, 0]],
]);

function fadeViewer(
  viewer: HTMLDialogElement,
  from: { panel: string; backdrop: string },
  to: '0' | '1',
) {
  const panel = viewer.animate([{ opacity: from.panel }, { opacity: to }], {
    duration: 200,
    easing: 'ease',
  });
  const backdrop = viewer.animate([{ opacity: from.backdrop }, { opacity: to }], {
    duration: 150,
    easing: 'ease',
    fill: to === '0' ? 'forwards' : 'none',
    pseudoElement: '::backdrop',
  });
  return { panel, backdrop };
}

export function ZoomableDiagram({
  src,
  alt,
  title,
  className,
}: {
  src: string;
  alt: string;
  title: string;
  className: string;
}) {
  const dialogId = useId();
  const titleId = useId();
  const scrollHintId = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const closing = useRef<Animation | null>(null);
  const [scale, setScale] = useState(1);

  const openViewer = () => {
    const viewer = dialog.current;
    if (!viewer) {
      return;
    }
    viewer.showModal();
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      fadeViewer(viewer, { panel: '0', backdrop: '0' }, '1');
    }
    closeButton.current?.focus();
  };

  const closeViewer = () => {
    const viewer = dialog.current;
    if (!viewer?.open || closing.current) {
      return;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      viewer.close();
      return;
    }

    const { panel, backdrop } = fadeViewer(
      viewer,
      {
        panel: getComputedStyle(viewer).opacity,
        backdrop: getComputedStyle(viewer, '::backdrop').opacity,
      },
      '0',
    );
    closing.current = panel;
    const finish = () => {
      if (dialog.current === viewer && closing.current === panel) {
        closing.current = null;
        viewer.close();
      }
      backdrop.cancel();
    };
    panel.addEventListener('finish', finish, { once: true });
    panel.addEventListener('cancel', finish, { once: true });
  };

  return (
    <>
      <button
        ref={opener}
        type="button"
        data-name="measure-diagram-open"
        aria-label={`${title} — open larger view`}
        aria-haspopup="dialog"
        aria-controls={dialogId}
        onClick={openViewer}
        className={`relative mx-auto mt-4 block w-full rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ${className}`}
      >
        <img src={src} alt={alt} className="h-auto w-full" />
        <span
          aria-hidden="true"
          className="absolute top-2 right-2 flex size-9 items-center justify-center rounded-full bg-white text-brand shadow-md ring-1 ring-black/5"
        >
          <svg
            className="size-[18px]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-4-4M11 8v6M8 11h6" />
          </svg>
        </span>
      </button>
      <dialog
        ref={dialog}
        id={dialogId}
        data-name="measure-diagram-viewer"
        aria-labelledby={titleId}
        aria-modal="true"
        onClose={() => {
          closing.current = null;
          setScale(1);
          opener.current?.focus();
        }}
        onCancel={(event) => {
          event.preventDefault();
          closeViewer();
        }}
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            closeViewer();
          }
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Tab') {
            return;
          }
          const controls = Array.from(
            event.currentTarget.querySelectorAll<HTMLElement>(
              'button:not([disabled]), [tabindex="0"]',
            ),
          ).filter((element) => element.getClientRects().length > 0);
          const first = controls[0];
          const last = controls.at(-1);
          const activeIndex = controls.findIndex((element) => element === document.activeElement);
          if (event.shiftKey && activeIndex <= 0 && last) {
            event.preventDefault();
            last.focus();
          } else if (
            !event.shiftKey &&
            (activeIndex === -1 || activeIndex === controls.length - 1) &&
            first
          ) {
            event.preventDefault();
            first.focus();
          }
        }}
        className="fixed inset-0 m-0 h-dvh max-h-none w-screen max-w-none overflow-hidden bg-white p-0 text-left text-ink shadow-xl backdrop:bg-black/60 md:m-auto md:h-auto md:max-h-[90dvh] md:w-[calc(100%-2rem)] md:max-w-2xl md:rounded-xl"
      >
        <div className="flex h-full max-h-[100dvh] flex-col md:max-h-[90dvh]">
          <div className="flex items-center justify-between gap-4 border-b border-gray-100 px-4 py-2">
            <h2 id={titleId} className="text-sm font-semibold">
              {title}
            </h2>
            <button
              ref={closeButton}
              type="button"
              data-name="measure-diagram-close"
              aria-label="Close"
              onClick={closeViewer}
              className="flex size-11 shrink-0 items-center justify-center rounded-lg text-gray-600 focus-visible:outline-2 focus-visible:outline-brand"
            >
              <svg
                aria-hidden="true"
                className="size-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div
            data-name="measure-diagram-canvas"
            role="region"
            aria-label="Scrollable measurement diagram"
            aria-describedby={scrollHintId}
            tabIndex={0}
            onKeyDown={(event) => {
              const direction = scrollDirections.get(event.key);
              if (
                direction &&
                !event.altKey &&
                !event.ctrlKey &&
                !event.metaKey &&
                !event.shiftKey
              ) {
                event.preventDefault();
                event.currentTarget.scrollBy(...direction);
              }
            }}
            className="min-h-0 flex-1 overflow-auto overscroll-contain bg-gray-50 focus-visible:outline-2 focus-visible:outline-brand md:h-[60dvh] md:flex-auto"
          >
            <span id={scrollHintId} className="sr-only">
              Use the arrow keys to move around the enlarged image.
            </span>
            <img
              src={src}
              alt={alt}
              draggable={false}
              style={{ width: `${scale * 100}%`, height: `${scale * 100}%` }}
              className="block max-w-none object-contain p-4"
            />
          </div>
          <div className="flex items-center justify-center gap-2 p-3">
            <button
              type="button"
              data-name="measure-diagram-zoom-out"
              aria-label="Zoom out"
              onClick={() => setScale((current) => Math.max(1, current - 0.5))}
              className="flex size-11 items-center justify-center rounded-lg border border-gray-200 text-gray-600 focus-visible:outline-2 focus-visible:outline-brand"
            >
              <svg
                aria-hidden="true"
                className="size-[18px]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M5 12h14" />
              </svg>
            </button>
            <button
              type="button"
              data-name="measure-diagram-zoom-in"
              aria-label="Zoom in"
              onClick={() => setScale((current) => Math.min(6, current + 0.5))}
              className="flex size-11 items-center justify-center rounded-lg border border-gray-200 text-gray-600 focus-visible:outline-2 focus-visible:outline-brand"
            >
              <svg
                aria-hidden="true"
                className="size-[18px]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M5 12h14M12 5v14" />
              </svg>
            </button>
            <button
              type="button"
              data-name="measure-diagram-reset"
              onClick={() => setScale(1)}
              className="flex min-h-11 items-center gap-1.5 rounded-lg border border-gray-200 px-3 text-xs font-medium text-gray-600 focus-visible:outline-2 focus-visible:outline-brand"
            >
              <svg
                aria-hidden="true"
                className="size-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5" />
              </svg>
              Reset
            </button>
            <span className="sr-only" aria-live="polite">
              Zoom {Math.round(scale * 100)}%
            </span>
          </div>
        </div>
      </dialog>
    </>
  );
}
